import React, { useState, useEffect } from 'react';

const CORNERS = ['FHD', 'FHN', 'BHD', 'BHN', 'WEDD', 'WEDN', 'AVG'];

const METRICS = [
  { key: 'direct_cplh', label: 'Direct CPLH', type: 'rate' },
  { key: 'diluted_cplh', label: 'Diluted CPLH', type: 'rate' },
  { key: 'ib_cplh', label: 'IB CPLH', type: 'rate' },
  { key: 'ob_cplh', label: 'OB CPLH', type: 'rate' },
  { key: 'sep1', label: '', type: 'separator' },
  { key: 'pid_dept', label: 'PID', type: 'rate' },
  { key: 'fluid_total', label: 'Fluid Total', type: 'rate' },
  { key: 'fluid_direct', label: 'Fluid Direct', type: 'rate' },
  { key: 'fluid_offline', label: 'Fluid Offline', type: 'rate' },
  { key: 'eol', label: 'EOL', type: 'rate' },
  { key: 'aros_ib', label: 'AROS Inbound', type: 'rate' },
  { key: 'aros_ob', label: 'AROS Outbound', type: 'rate' },
  { key: 'ib_pr', label: 'IB PR', type: 'rate' },
  { key: 'pit', label: 'PIT', type: 'rate' },
  { key: 'sep2', label: '', type: 'separator' },
  { key: 'total_ib_cartons', label: 'Total IB Cartons', type: 'cartons' },
  { key: 'total_ob_cartons', label: 'Total OB Cartons', type: 'cartons' },
  { key: 'sorter_cartons', label: 'Sorter Cartons', type: 'cartons' },
  { key: 'aros_cartons', label: 'AROS Cartons', type: 'cartons' },
  { key: 'pr_cartons', label: 'PR Cartons', type: 'cartons' },
  { key: 'fluid_cartons', label: 'Fluid Cartons', type: 'cartons' },
  { key: 'pit_cartons', label: 'PIT Cartons', type: 'cartons' },
];

function extractMetric(actual, key) {
  if (key in actual && actual[key] !== undefined && actual[key] !== null) {
    const val = parseFloat(actual[key]);
    if (!isNaN(val)) return val;
  }
  const c = actual.cartons || {};
  const ppr = actual.ppr_hours || {};
  const labor = actual.labor || [];
  const fcHrs = Object.values(ppr.fcSummary || {}).reduce((s, v) => s + v, 0);
  const ibHrs = Object.values(ppr.inbound || {}).reduce((s, v) => s + v, 0);
  const obHrs = Object.values(ppr.transferOut || {}).reduce((s, v) => s + v, 0);
  const ibCartons = c.inbound?.total || 0;
  const obCartons = c.outbound?.total || 0;
  const getLaborHrs = (name) => { const r = labor.find(l => l.process_path === name); return r?.total || 0; };
  const sumLaborHrs = (names) => names.reduce((s, n) => s + getLaborHrs(n), 0);
  const deptRate = (names, cartons) => { const hrs = sumLaborHrs(names); return hrs > 0 ? cartons / hrs : 0; };
  const pidCartons = c.pid?.total || 0;
  const arosCartons = c.aros?.total || 0;
  const prCartons = c.pr?.total || 0;
  const fluidDirect = c.fluidDirect?.total || 0;
  const fluidOffline = c.fluidOffline?.total || 0;
  const pitCartons = c.pit?.total || 0;
  const eolCartons = c.eol?.total ?? Math.max(0, pidCartons - fluidDirect);
  const map = {
    direct_cplh: (ibHrs + obHrs) > 0 ? (ibCartons + obCartons) / (ibHrs + obHrs) : 0,
    diluted_cplh: fcHrs > 0 ? (ibCartons + obCartons) / fcHrs : 0,
    ib_cplh: ibHrs > 0 ? ibCartons / ibHrs : 0,
    ob_cplh: obHrs > 0 ? obCartons / obHrs : 0,
    pid_dept: deptRate(['PID Unload', 'PID Divert', 'PID Reinject', 'PID Operator'], pidCartons),
    fluid_total: deptRate(['Sorter Wallbuild', 'Jam Clear', 'Fluid WS', 'Offline Wallbuild'], fluidDirect + fluidOffline),
    fluid_direct: deptRate(['Sorter Wallbuild'], fluidDirect),
    fluid_offline: deptRate(['Offline Wallbuild', 'Fluid WS'], fluidOffline),
    eol: deptRate(['EOL Avery', 'EOL Divert', 'EOL Sort', 'EOL Waterspider'], eolCartons),
    aros_ib: deptRate(['AROS Unload', 'AROS Operator', 'AROS CSX Label'], arosCartons),
    aros_ob: deptRate(['AROS Avery', 'AROS Divert', 'AROS Sort', 'AROS Waterspider'], arosCartons),
    ib_pr: deptRate(['PR Unload', 'Pallet Build', 'Wrap/Lantech', 'PR Waterspider', 'IB Pallet EPJ WS'], prCartons),
    pit: deptRate(['PIT Unloader', 'OB EPJ WaterSpider', 'Pallet Loader', 'OB EPJ Operator'], pitCartons),
    total_ib_cartons: ibCartons, total_ob_cartons: obCartons,
    sorter_cartons: pidCartons - fluidDirect, aros_cartons: arosCartons,
    pr_cartons: prCartons, fluid_cartons: fluidDirect + fluidOffline, pit_cartons: pitCartons,
  };
  return map[key] || 0;
}

function deriveCorner(date, shift_type) {
  const d = new Date(date + 'T12:00:00');
  const day = d.getDay();
  const isNight = shift_type === 'night';
  if (day === 3) return isNight ? 'WEDN' : 'WEDD'; // Wednesday
  if (day >= 0 && day <= 2) return isNight ? 'FHN' : 'FHD'; // Sun-Tue
  return isNight ? 'BHN' : 'BHD'; // Thu-Sat
}

function computeCornerData(actuals) {
  const cornerData = {};
  for (const corner of CORNERS.filter(c => c !== 'AVG')) {
    const cornerActuals = actuals.filter(a => a.corner === corner);
    cornerData[corner] = {};
    for (const m of METRICS) {
      if (m.type === 'separator') continue;
      const values = cornerActuals.map(a => extractMetric(a, m.key)).filter(v => v > 0);
      cornerData[corner][m.key] = values.length > 0 ? values.reduce((s, v) => s + v, 0) / values.length : 0;
    }
  }
  cornerData['AVG'] = {};
  for (const m of METRICS) {
    if (m.type === 'separator') continue;
    const allVals = CORNERS.filter(c => c !== 'AVG').map(c => cornerData[c][m.key]).filter(v => v > 0);
    cornerData['AVG'][m.key] = allVals.length > 0 ? allVals.reduce((s, v) => s + v, 0) / allVals.length : 0;
  }
  return cornerData;
}

function getHeatColor(value, avg) {
  if (!avg || !value) return '#f5f5f5';
  const ratio = value / avg;
  if (ratio >= 1.05) return '#c8e6c9';
  if (ratio >= 1.0) return '#e8f5e9';
  if (ratio >= 0.95) return '#fff9c4';
  if (ratio >= 0.90) return '#ffcc80';
  return '#ef9a9a';
}

function getDeltaColor(delta) {
  if (!delta) return '#f5f5f5';
  if (delta > 0) return '#c8e6c9';
  if (delta < 0) return '#ef9a9a';
  return '#f5f5f5';
}

function DataTable({ title, cornerData, cellStyle }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h3 className="section-title">{title}</h3>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8rem', border: '1px solid #000' }}>
        <thead>
          <tr>
            <th style={{ ...cellStyle, textAlign: 'left', background: '#232f3e', color: '#fff', position: 'sticky', left: 0, zIndex: 1 }}>Metric</th>
            {CORNERS.map(c => <th key={c} style={{ ...cellStyle, background: '#232f3e', color: '#fff' }}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {METRICS.map(m => {
            if (m.type === 'separator') return <tr key={m.key}><td colSpan={8} style={{ height: 8 }}></td></tr>;
            const avg = cornerData['AVG']?.[m.key] || 0;
            return (
              <tr key={m.key}>
                <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>{m.label}</td>
                {CORNERS.map(c => {
                  const val = cornerData[c]?.[m.key] || 0;
                  const bg = c === 'AVG' ? '#e3f2fd' : getHeatColor(val, avg);
                  return <td key={c} style={{ ...cellStyle, background: bg }}>{val ? (m.type === 'cartons' ? Math.round(val).toLocaleString() : val.toFixed(1)) : '—'}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DeltaTable({ title, data1, data2, cellStyle }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h3 className="section-title">{title}</h3>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8rem', border: '1px solid #000' }}>
        <thead>
          <tr>
            <th style={{ ...cellStyle, textAlign: 'left', background: '#232f3e', color: '#fff', position: 'sticky', left: 0, zIndex: 1 }}>Metric</th>
            {CORNERS.map(c => <th key={c} style={{ ...cellStyle, background: '#232f3e', color: '#fff' }}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {METRICS.map(m => {
            if (m.type === 'separator') return <tr key={m.key}><td colSpan={8} style={{ height: 8 }}></td></tr>;
            return (
              <tr key={m.key}>
                <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>{m.label}</td>
                {CORNERS.map(c => {
                  const v1 = data1[c]?.[m.key] || 0;
                  const v2 = data2[c]?.[m.key] || 0;
                  const delta = v2 && v1 ? v2 - v1 : 0;
                  const pctChange = v1 > 0 ? ((v2 - v1) / v1) * 100 : 0;
                  const bg = getDeltaColor(delta);
                  const fmt = pctChange ? (pctChange > 0 ? '+' : '') + pctChange.toFixed(1) + '%' : '—';
                  return <td key={c} style={{ ...cellStyle, background: bg, color: delta > 0 ? '#2e7d32' : delta < 0 ? '#c62828' : '#666' }}>{fmt}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function Heatmap({ api, site }) {
  const [range1From, setRange1From] = useState('');
  const [range1To, setRange1To] = useState('');
  const [range2From, setRange2From] = useState('');
  const [range2To, setRange2To] = useState('');
  const [actuals1, setActuals1] = useState([]);
  const [actuals2, setActuals2] = useState([]);
  const [label1, setLabel1] = useState('Range 1');
  const [label2, setLabel2] = useState('Range 2');
  const [excludeWed, setExcludeWed] = useState(false);

  // Default to last month vs this month
  useEffect(() => {
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const thisMonthEnd = now.toISOString().split('T')[0];
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
    setRange1From(lastMonthStart); setRange1To(lastMonthEnd);
    setRange2From(thisMonthStart); setRange2To(thisMonthEnd);
    setLabel1(`${new Date(lastMonthStart).toLocaleString('default', { month: 'long' })}`);
    setLabel2(`${new Date(thisMonthStart).toLocaleString('default', { month: 'long' })}`);
  }, []);

  useEffect(() => {
    if (range1From && range1To) {
      const dates1 = [];
      let dd1 = new Date(range1From + 'T12:00:00');
      const end1 = new Date(range1To + 'T12:00:00');
      while (dd1 <= end1) { dates1.push(dd1.toISOString().split('T')[0]); dd1.setDate(dd1.getDate() + 1); }
      Promise.all(dates1.flatMap(date => ['day','night'].map(shift =>
        fetch(`${api}/api/fclm/cache?site=${site}&date=${date}&shift_type=${shift}`)
          .then(r => r.json()).then(data => data?.labor ? { ...data, date, shift_type: shift, site } : null).catch(() => null)
      ))).then(results => {
        const data = results.filter(Boolean);
        const withCorners = data.map(a => ({ ...a, corner: deriveCorner(a.date, a.shift_type) }));
        const deduped = []; const seen = {};
        for (const item of withCorners) { const key = `${item.date}#${item.shift_type}`; if (!seen[key]) { seen[key] = item; deduped.push(item); } else if (item.cartons && !seen[key].cartons) { deduped[deduped.indexOf(seen[key])] = item; seen[key] = item; } }
        setActuals1(deduped);
      }).catch(() => setActuals1([]));
      setLabel1(getLabel(range1From, range1To));
    }
  }, [api, site, range1From, range1To]);

  useEffect(() => {
    if (range2From && range2To) {
      const dates2 = [];
      let dd2 = new Date(range2From + 'T12:00:00');
      const end2 = new Date(range2To + 'T12:00:00');
      while (dd2 <= end2) { dates2.push(dd2.toISOString().split('T')[0]); dd2.setDate(dd2.getDate() + 1); }
      Promise.all(dates2.flatMap(date => ['day','night'].map(shift =>
        fetch(`${api}/api/fclm/cache?site=${site}&date=${date}&shift_type=${shift}`)
          .then(r => r.json()).then(data => data?.labor ? { ...data, date, shift_type: shift, site } : null).catch(() => null)
      ))).then(results => {
        const data = results.filter(Boolean);
        const withCorners = data.map(a => ({ ...a, corner: deriveCorner(a.date, a.shift_type) }));
        const deduped = []; const seen = {};
        for (const item of withCorners) { const key = `${item.date}#${item.shift_type}`; if (!seen[key]) { seen[key] = item; deduped.push(item); } else if (item.cartons && !seen[key].cartons) { deduped[deduped.indexOf(seen[key])] = item; seen[key] = item; } }
        setActuals2(deduped);
      }).catch(() => setActuals2([]));
      setLabel2(getLabel(range2From, range2To));
    }
  }, [api, site, range2From, range2To]);

  function getLabel(from, to) {
    const f = new Date(from);
    const t = new Date(to);
    if (f.getMonth() === t.getMonth()) return f.toLocaleString('default', { month: 'long', year: 'numeric' });
    return `${f.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${t.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  }

  const cornerData1 = computeCornerData(excludeWed ? actuals1.filter(a => new Date(a.date + 'T12:00:00').getDay() !== 3) : actuals1);
  const cornerData2 = computeCornerData(excludeWed ? actuals2.filter(a => new Date(a.date + 'T12:00:00').getDay() !== 3) : actuals2);

  const cellStyle = { padding: '4px 6px', textAlign: 'center', fontSize: '0.7rem', fontWeight: 500, border: '1px solid #000' };

  return (
    <div>
      <h2 className="section-title">Heatmap Comparison — {site}</h2>
      <div className="card">
        <div className="form-row" style={{ flexWrap: 'wrap' }}>
          <div className="form-group">
            <label>Range 1 From</label>
            <input type="date" value={range1From} onChange={e => setRange1From(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Range 1 To</label>
            <input type="date" value={range1To} onChange={e => setRange1To(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Range 2 From</label>
            <input type="date" value={range2From} onChange={e => setRange2From(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Range 2 To</label>
            <input type="date" value={range2To} onChange={e => setRange2To(e.target.value)} />
          </div>
          <div className="form-group" style={{ justifyContent: 'flex-end' }}>
            <button className={`btn ${excludeWed ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setExcludeWed(!excludeWed)} style={{ fontSize: '0.8rem', padding: '4px 12px' }}>
              Excl. Wed {excludeWed ? 'On' : 'Off'}
            </button>
          </div>
        </div>
        <p style={{ fontSize: '0.8rem', color: '#666' }}>{label1}: {actuals1.length} shifts | {label2}: {actuals2.length} shifts</p>
      </div>

      <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
        <div className="card" style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
          <DataTable title={label1} cornerData={cornerData1} cellStyle={cellStyle} />
        </div>
        <div className="card" style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
          <DataTable title={label2} cornerData={cornerData2} cellStyle={cellStyle} />
        </div>
        <div className="card" style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
          <DeltaTable title={`Δ (${label2} - ${label1})`} data1={cornerData1} data2={cornerData2} cellStyle={cellStyle} />
        </div>
      </div>
    </div>
  );
}
